import { useEmbarcacaoConsumoOperacao } from "@/modules/embarcacoes";
import type {
  EmbarcacaoConsumoOperacao,
  EmbarcacaoConsumoOperacaoField,
} from "@/modules/embarcacoes";
import type { EmptyStateContent } from "@/screens/control-panel/components/empty-state";
import {
  DetailField,
  DetailSection,
  DetailSectionFields,
  DetailSectionState,
} from "../detail-section";
import { formatDetailDate } from "../embarcacao-detail-format";
import type { EmbarcacaoDetailSectionProps } from "../embarcacao-detail-section";

export const CONSUMO_OPERACAO_ID = "consumo-operacao";
export const CONSUMO_OPERACAO_TITLE = "Consumo Modo Operação";
export const CONSUMO_OPERACAO_LOADING_MESSAGE =
  "Carregando o Consumo Modo Operação da embarcação...";

/** Unidade de todos os consumos da seção (RN002). */
export const CONSUMO_UNIT = "m³/dia";

/** Padrão global de estado vazio do Hub para a seção sem informações (AC3). */
const CONSUMO_OPERACAO_EMPTY_STATE: EmptyStateContent = {
  title: "Nenhuma informação de Consumo Modo Operação disponível",
  description:
    "Assim que houver dados de consumo para esta embarcação, eles aparecerão nesta seção.",
};

const TEST_ID = "embarcacao-consumo-operacao";

/** Consumo como recebido, sem recálculo ou unidade no valor (AC2). */
function formatConsumo(value: string): string {
  return value;
}

interface ConsumoOperacaoFieldDefinition {
  readonly key: EmbarcacaoConsumoOperacaoField;
  readonly label: string;
  readonly format: (value: string) => string;
}

/**
 * Os 7 campos do corpo da seção, na ordem do protótipo (AC2). O grid os
 * distribui em duas colunas, par a par. A última atualização acompanha o
 * título, como nas demais seções.
 */
export const CONSUMO_OPERACAO_FIELDS: ConsumoOperacaoFieldDefinition[] = [
  { key: "consumoDp", label: `Consumo DP (${CONSUMO_UNIT})`, format: formatConsumo },
  { key: "consumoFdAt", label: `Consumo FD/AT (${CONSUMO_UNIT})`, format: formatConsumo },
  {
    key: "consumoNvVelServico",
    label: `Consumo NV em Vel. Serviço (${CONSUMO_UNIT})`,
    format: formatConsumo,
  },
  {
    key: "consumoNvVelTotal",
    label: `Consumo NV em Vel. Total (${CONSUMO_UNIT})`,
    format: formatConsumo,
  },
  { key: "consumoSm", label: `Consumo SM (${CONSUMO_UNIT})`, format: formatConsumo },
  {
    key: "consumoNvVelEconomica",
    label: `Consumo NV em Vel. Econômica (${CONSUMO_UNIT})`,
    format: formatConsumo,
  },
  { key: "atualizacaoConsumo", label: "Data Atualização Consumo", format: formatDetailDate },
];

/** Valor do campo pronto para a tela; `null` segue como campo sem preenchimento. */
function fieldValue(
  consumo: EmbarcacaoConsumoOperacao,
  { key, format }: ConsumoOperacaoFieldDefinition,
): string | null {
  const value = consumo[key];

  return value === null ? null : format(value);
}

/**
 * Seção Consumo Modo Operação do detalhamento da embarcação (US-1795).
 *
 * Exclusivamente de leitura: nenhum botão de cadastro, edição, exclusão ou
 * exportação (AC5). Os consumos são apresentados como recebidos, com a unidade
 * m³/dia nas labels, e o campo sem valor mostra "Campo sem preenchimento" (AC3).
 */
export function ConsumoOperacaoSection({
  embarcacaoId,
  onReauthenticate,
}: EmbarcacaoDetailSectionProps) {
  const { status, consumo, errorMessage, reload } = useEmbarcacaoConsumoOperacao(embarcacaoId);

  return (
    <DetailSection
      id={CONSUMO_OPERACAO_ID}
      title={CONSUMO_OPERACAO_TITLE}
      ultimaAtualizacao={consumo ? consumo.ultimaAtualizacao : undefined}
      testId={TEST_ID}
    >
      {consumo ? (
        <DetailSectionFields testId={`${TEST_ID}-fields`}>
          {CONSUMO_OPERACAO_FIELDS.map((field) => (
            <DetailField
              key={field.key}
              label={field.label}
              value={fieldValue(consumo, field)}
              testId={`${TEST_ID}-${field.key}`}
            />
          ))}
        </DetailSectionFields>
      ) : (
        <DetailSectionState
          // `success` sempre traz os campos: aqui só chegam carregamento e exceções.
          status={status === "success" ? "error" : status}
          loadingMessage={CONSUMO_OPERACAO_LOADING_MESSAGE}
          errorMessage={errorMessage}
          emptyContent={CONSUMO_OPERACAO_EMPTY_STATE}
          onReauthenticate={onReauthenticate}
          onRetry={reload}
          testId={TEST_ID}
        />
      )}
    </DetailSection>
  );
}
